import { z } from 'zod'
import dayjs from 'dayjs'
import { DocumentSubType } from '@@api/shared'

export const MAX_DOCUMENT_FILE_SIZE = 150 * 1024 * 1024 // 150MB

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// In-form expiry hint (1-month window). The document *list* cards use the
// 2-month-window getDocumentExpiryStatus in DocumentListCard.tsx.
export function getExpiryHintStatus(
  expiryDate: Date | null | undefined
): 'expired' | 'expiring' | 'valid' | null {
  if (!expiryDate) return null
  const now = dayjs()
  const expiry = dayjs(expiryDate)
  if (expiry.isBefore(now)) return 'expired'
  if (expiry.isBefore(now.add(1, 'month'))) return 'expiring'
  return 'valid'
}

// Shared form shape for every document create/edit drawer. Fields a given
// owner doesn't render just stay at their defaults; the expiry>issue refine
// passes vacuously when issueDate isn't shown.
export const documentFormSchema = z
  .object({
    title: z.string().min(1, 'Document title is required'),
    subType: z.nativeEnum(DocumentSubType).optional(),
    documentNumber: z.string().optional(),
    issuer: z.string().optional(),
    version: z.string().optional(),
    issueDate: z.date().nullable().optional(),
    expiryDate: z.date().nullable().optional(),
    isPublic: z.boolean().default(false),
    file: z.instanceof(File, { message: 'Please select a file to upload' }).nullable(),
  })
  .refine(
    (data) => {
      if (!data.issueDate || !data.expiryDate) return true
      return data.expiryDate > data.issueDate
    },
    {
      message: 'Expiry date must be after issue date',
      path: ['expiryDate'],
    }
  )

export type DocumentFormData = z.infer<typeof documentFormSchema>
